import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { CaretDown, ArrowsDownUp, MagnifyingGlass, MapPin, Lightning, NavigationArrow, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { passengerApi } from "@/lib/passengerApi";
import { MajheHeader } from "@/components/majhe/MajheLayout";

const PUBLIC_API = `${process.env.REACT_APP_BACKEND_URL}/api/public`;

export default function BookBus() {
  const [pickup, setPickup] = useState("");
  const [drop, setDrop] = useState("");
  const [activeTickets, setActiveTickets] = useState([]);
  const [pickerMode, setPickerMode] = useState(null);   // 'pickup' | 'drop' | null
  const [showNearby, setShowNearby] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    passengerApi.get("/me/tickets?status=issued")
      .then(({ data }) => setActiveTickets(data.filter((t) => t.status === "issued").slice(0, 5)))
      .catch(() => {});
  }, []);

  const swap = () => { setPickup(drop); setDrop(pickup); };

  const search = () => {
    if (!pickup.trim() || !drop.trim()) { toast.error("Please enter pickup and drop"); return; }
    navigate(`/majhe/book/results?pickup=${encodeURIComponent(pickup)}&drop=${encodeURIComponent(drop)}`);
  };

  const handlePick = (stopName) => {
    if (pickerMode === "pickup") setPickup(stopName);
    else if (pickerMode === "drop") setDrop(stopName);
    setPickerMode(null);
  };

  const handleNearbyPick = (stopName) => {
    setPickup(stopName);
    setShowNearby(false);
  };

  return (
    <div data-testid="majhe-book-bus">
      <MajheHeader title="Select Location" onBack={() => navigate("/majhe")}/>

      <div className="px-5 pt-2 space-y-3">
        <LocationField icon={<NavigationArrow size={20} weight="fill"/>}
          placeholder="Pickup Location"
          value={pickup} onChange={setPickup}
          onTapAside={() => setPickerMode("pickup")}
          tid="pickup-field"/>

        <div className="flex justify-end -my-1">
          <button onClick={swap} data-testid="swap-btn"
            type="button"
            aria-label="Swap pickup and drop locations"
            className="w-10 h-10 rounded-lg bg-[#EDE5FF] text-[#5B2EE5] flex items-center justify-center border border-[#DCC9FF]">
            <ArrowsDownUp size={18} weight="bold" aria-hidden="true"/>
          </button>
        </div>

        <LocationField icon={<MapPin size={20} weight="fill"/>}
          placeholder="Drop Location"
          value={drop} onChange={setDrop}
          onTapAside={() => setPickerMode("drop")}
          tid="drop-field"/>

        <button data-testid="search-buses-btn" onClick={search}
          type="button" aria-label="Search available buses for the selected pickup and drop"
          className="majhe-btn mt-3">
          <MagnifyingGlass size={18} weight="bold" aria-hidden="true"/> Search Buses
        </button>
      </div>

      <div className="px-5 mt-8">
        <h3 className="majhe-section-title">Your active tickets</h3>
        {activeTickets.length === 0 ? (
          <EmptyTickets/>
        ) : (
          <div className="space-y-2.5">
            {activeTickets.map((t) => (
              <div key={t.id} data-testid="active-ticket-row"
                className="bg-white border border-[#EDE8FA] rounded-2xl p-4 flex items-center gap-3 cursor-pointer"
                onClick={() => navigate(`/majhe/tickets/${t.ticket_no}`)}>
                <div className="w-11 h-11 rounded-xl bg-[#EDE5FF] text-[#5B2EE5] flex items-center justify-center">
                  <Lightning size={20} weight="fill"/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm text-[#18142A] truncate">{t.route_name}</div>
                  <div className="text-[11px] text-[#7B7390] mt-0.5">
                    {t.from_stop} → {t.to_stop} · {t.passengers}p · ₹{t.total_paid}
                  </div>
                </div>
                <span className="majhe-pill majhe-pill--green">Active</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Sticky Nearby Bus Stops CTA (uses geolocation to autofill Pickup only) */}
      <div className="px-5 mt-8 pb-6">
        <button data-testid="nearby-stops-btn" onClick={() => setShowNearby(true)}
          type="button" aria-label="Use my phone location to find the nearest bus stops for pickup"
          className="majhe-btn majhe-btn--ghost">
          <MapPin size={18} weight="bold" aria-hidden="true"/> Use Nearby Bus Stops (Pickup)
        </button>
        <div className="text-[10.5px] text-center text-[#7B7390] mt-2 leading-relaxed">
          Uses your phone&apos;s location to suggest the closest pickup stop
        </div>
      </div>

      {pickerMode && (
        <StopPickerSheet
          target={pickerMode}
          onClose={() => setPickerMode(null)}
          onPick={handlePick}/>
      )}
      {showNearby && (
        <NearbyStopsSheet onClose={() => setShowNearby(false)} onPick={handleNearbyPick}/>
      )}
    </div>
  );
}

function LocationField({ icon, value, onChange, placeholder, onTapAside, tid }) {
  const inputId = `field-${tid}`;
  return (
    <div className="majhe-field" data-testid={tid}>
      <div className="majhe-field__icon" aria-hidden="true">{icon}</div>
      <label htmlFor={inputId} className="sr-only">{placeholder}</label>
      <input id={inputId} className="majhe-input" placeholder={placeholder}
        style={{ paddingRight: 38 }}
        aria-label={placeholder}
        value={value} onChange={(e) => onChange(e.target.value)}
        data-testid={`${tid}-input`}/>
      <button onClick={onTapAside} data-testid={`${tid}-nearby`}
        type="button"
        aria-label={`Open bus stop search for ${placeholder.toLowerCase()}`}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5B2EE5]">
        <CaretDown size={18} weight="bold" aria-hidden="true"/>
      </button>
    </div>
  );
}

function EmptyTickets() {
  return (
    <div data-testid="empty-tickets" className="flex flex-col items-center text-center py-10">
      <svg width="120" height="120" viewBox="0 0 100 100" fill="none">
        <rect x="22" y="28" width="56" height="60" rx="6" stroke="#5B2EE5" strokeWidth="3.5"/>
        <path d="M28 38 L72 38 M28 48 L72 48 M28 58 L60 58" stroke="#5B2EE5" strokeWidth="3.5" strokeLinecap="round"/>
        <path d="M72 28 L82 28 L82 38" stroke="#5B2EE5" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"/>
        <circle cx="78" cy="74" r="11" fill="white" stroke="#5B2EE5" strokeWidth="3.5"/>
        <path d="M78 68 L78 76" stroke="#5B2EE5" strokeWidth="3" strokeLinecap="round"/>
        <circle cx="78" cy="80" r="1.4" fill="#5B2EE5"/>
      </svg>
      <div className="text-[#18142A] font-semibold mt-3">No active tickets</div>
      <div className="text-[12px] text-[#7B7390] mt-1">Book a bus above to get started</div>
    </div>
  );
}

/* ============================================================
   StopPickerSheet — searchable list of ALL bus stops
   Used for both Pickup and Drop chevron taps
============================================================ */
function StopPickerSheet({ target, onClose, onPick }) {
  const [q, setQ] = useState("");
  const [stops, setStops] = useState([]);
  const [loading, setLoading] = useState(true);
  const inputRef = useRef(null);

  // Debounced search
  useEffect(() => {
    const id = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await fetch(`${PUBLIC_API}/stops/search?q=${encodeURIComponent(q)}&limit=60`);
        const data = await res.json();
        setStops(data);
      } finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(id);
  }, [q]);

  useEffect(() => { setTimeout(() => inputRef.current?.focus(), 200); }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-end" style={{ background: "rgba(15,8,40,0.55)" }}
      onClick={onClose} data-testid="stop-picker-sheet"
      role="dialog" aria-modal="true" aria-labelledby="stop-picker-title">
      <div className="bg-white w-full rounded-t-[28px] overflow-hidden flex flex-col"
        style={{ maxHeight: "85dvh" }} onClick={(e) => e.stopPropagation()}>
        <div className="px-5 pt-5 pb-3 border-b border-[#F0EBFC] sticky top-0 bg-white z-10">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-[#7B7390] font-bold">
                Choose your {target === "pickup" ? "pickup" : "drop"}
              </div>
              <h2 id="stop-picker-title" className="text-lg font-extrabold text-[#18142A]">
                {target === "pickup" ? "Pickup Bus Stop" : "Drop Bus Stop"}
              </h2>
            </div>
            <button onClick={onClose} data-testid="stop-picker-close"
              type="button"
              aria-label="Close bus stop search"
              className="w-9 h-9 rounded-lg bg-slate-100 text-[#7B7390]"><X size={20} aria-hidden="true"/></button>
          </div>
          <div className="relative">
            <MagnifyingGlass size={16} aria-hidden="true" className="absolute left-4 top-1/2 -translate-y-1/2 text-[#7B7390]"/>
            <label htmlFor="stop-search" className="sr-only">Search bus stops by name</label>
            <input id="stop-search" ref={inputRef} data-testid="stop-search-input"
              value={q} onChange={(e) => setQ(e.target.value)}
              aria-label="Search bus stops by name"
              placeholder="Type bus stop name…"
              className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-full text-sm font-medium outline-none focus:border-[#5B2EE5] focus:bg-white"/>
          </div>
        </div>
        <div className="overflow-y-auto p-3 flex-1 majhe-sheet-body" role="region" aria-live="polite" aria-busy={loading}>
          {loading && (
            <div className="space-y-2 p-2">
              <span className="sr-only">Loading bus stops…</span>
              {[0, 1, 2, 3, 4].map((i) => <div key={i} className="majhe-skel h-12" aria-hidden="true"/>)}
            </div>
          )}
          {!loading && stops.length === 0 && (
            <div className="text-center text-[#7B7390] text-sm py-12" role="status">
              No bus stops match &quot;{q}&quot;
            </div>
          )}
          {!loading && stops.map((s) => (
            <button key={s.id} data-testid={`stop-picker-${s.id}`}
              type="button"
              onClick={() => onPick(s.name)}
              aria-label={`Select ${s.name}, bus stop code ${s.code || "unknown"}`}
              className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-[#F5F2FF] transition-colors text-left">
              <div className="w-10 h-10 rounded-full bg-[#EDE5FF] text-[#5B2EE5] flex items-center justify-center shrink-0" aria-hidden="true">
                <MapPin size={18} weight="fill"/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm text-[#18142A] truncate">{s.name}</div>
                <div className="text-[11px] text-[#7B7390]">Stop · {s.code || "—"}</div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   NearbyStopsSheet — geolocation-based for Pickup autofill only
============================================================ */
function NearbyStopsSheet({ onClose, onPick }) {
  const [stops, setStops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    const load = async (lat, lng) => {
      try {
        const { data } = await passengerApi.get(`/stops/nearby?lat=${lat}&lng=${lng}&limit=10`);
        setStops(data);
      } catch (e) { setErr("Failed to load nearby stops"); }
      finally { setLoading(false); }
    };
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => load(pos.coords.latitude, pos.coords.longitude),
        () => load(15.4909, 73.8278), // fallback Panaji
        { enableHighAccuracy: false, timeout: 4000 }
      );
    } else {
      load(15.4909, 73.8278);
    }
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-end" style={{ background: "rgba(15,8,40,0.55)" }}
      onClick={onClose} data-testid="nearby-sheet"
      role="dialog" aria-modal="true" aria-labelledby="nearby-title">
      <div className="bg-white w-full rounded-t-[28px] overflow-hidden" style={{ maxHeight: "80dvh" }}
        onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-[#F0EBFC] flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-[#7B7390] font-bold">Tap to fill Pickup</div>
            <h2 id="nearby-title" className="text-lg font-extrabold text-[#18142A]">Nearby Bus Stops</h2>
          </div>
          <button onClick={onClose} type="button" aria-label="Close nearby bus stops"
            className="text-[#7B7390] font-bold text-2xl leading-none px-2">×</button>
        </div>
        <div className="overflow-y-auto p-3 majhe-sheet-body" style={{ maxHeight: "65dvh" }}
          role="region" aria-live="polite" aria-busy={loading}>
          {loading && (
            <div className="space-y-2 p-2">
              <span className="sr-only">Finding nearby bus stops…</span>
              {[0, 1, 2, 3].map((i) => <div key={i} className="majhe-skel h-14" aria-hidden="true"/>)}
            </div>
          )}
          {err && <div className="text-red-600 text-sm p-4 text-center" role="alert">{err}</div>}
          {!loading && stops.map((s) => (
            <button key={s.id} data-testid={`nearby-stop-${s.id}`}
              type="button"
              onClick={() => onPick(s.name)}
              aria-label={`Select ${s.name} for pickup, ${s.distance_label} away`}
              className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-[#F5F2FF] transition-colors text-left">
              <div className="w-10 h-10 rounded-full bg-[#EDE5FF] text-[#5B2EE5] flex items-center justify-center" aria-hidden="true">
                <MapPin size={18} weight="fill"/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm text-[#18142A] truncate">{s.name}</div>
                <div className="text-[11px] text-[#7B7390]">Stop · {s.code || "—"}</div>
              </div>
              <div className="text-right" aria-hidden="true">
                <div className="text-[12px] font-extrabold text-[#5B2EE5]">{s.distance_label}</div>
                <div className="text-[10px] text-[#7B7390]">away</div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
